import BaseModal from '@/components/BaseModal';
import BoldSmallText from '@/components/BoldSmallText';
import RegularText from '@/components/RegularText';
import Required from '@/components/Required';
import { newLimitPlan } from '@/constants';
import { Plan } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { openCrisp } from '@/helpers';
import { Banner, Checkbox, Link, List } from '@shopify/polaris';
import { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
interface IProps {
  open: boolean;
  plan: 'free' | 'standard' | 'pro';
  onClose: () => void;
  onConfirm: () => void;
}

const DowngradeModal = (props: IProps) => {
  const navigate = useNavigate();
  const [isChecked, setIsChecked] = useState(false);

  const configModal = {
    free: (
      <RegularText>
        Downgrading to <BoldSmallText>Free Plan</BoldSmallText> will limit you to:
        <List>
          <List.Item>
            <BoldSmallText>{newLimitPlan[Plan.FREE].maxRule}</BoldSmallText> estimate rule
          </List.Item>
          <List.Item>No custom rule by item and location conditions</List.Item>
          <List.Item>No custom visual</List.Item>
          <List.Item>and many advanced features</List.Item>
        </List>
      </RegularText>
    ),
    standard: (
      <RegularText>
        Downgrading to <BoldSmallText>Standard Plan</BoldSmallText> will limit you to:
        <List>
          <List.Item>
            <BoldSmallText>{newLimitPlan[Plan.STANDARD].maxRule}</BoldSmallText> estimate rules
          </List.Item>
          <List.Item>No custom rule by location conditions</List.Item>
          <List.Item>Standard auto-detect location</List.Item>
          <List.Item>and many advanced features</List.Item>
        </List>
      </RegularText>
    ),
    pro: (
      <RegularText>
        Downgrading to <BoldSmallText>Pro Plan</BoldSmallText> will limit you to:
        <List>
          <List.Item>No ETA checkout extension</List.Item>
          <List.Item>Slower handling high traffic store</List.Item>
          <List.Item>Less priority support</List.Item>
        </List>
      </RegularText>
    ),
  };

  return (
    <BaseModal
      title="Review plan downgrade and data storage policy"
      open={props.open}
      onClose={() => {
        props.onClose();
        setIsChecked(false);
      }}
      secondaryActions={[
        {
          content: 'Stay on current plan',
          onAction: () => {
            props.onClose();
            setIsChecked(false);
          },
          // @ts-ignore
          variant: 'primary',
        },
        {
          content: 'Confirm downgrade',
          onAction: () => {
            props.onConfirm();
            props.onClose();
            setIsChecked(false);
          },
          disabled: !isChecked,
        },
      ]}
    >
      {configModal[props.plan]}
      <div className="mt-16">
        <Banner
          tone="warning"
          secondaryAction={{
            content: 'Review settings',
            onAction: () => navigate(PATH.RULE_SETUP),
          }}
        >
          <RegularText>
            <BoldSmallText>Important:</BoldSmallText> Your existing data - Latest created rules and targets (countries, regions,
            zipcodes, products, collections, vendors), if any, that are{' '}
            <BoldSmallText>
              beyond limit of{' '}
              {props.plan === Plan.FREE ? 'Free Plan' : props.plan === Plan.STANDARD ? 'Standard Plan' : 'Pro Plan'} will be
              permanently deleted
            </BoldSmallText>{' '}
            and cannot be recovered. Please review your current settings before taking further action.
          </RegularText>
        </Banner>
        <div className="mt-8">
          <RegularText>
            Please <Link onClick={openCrisp}>contact support</Link> if you have additional questions.
          </RegularText>
        </div>
        <Checkbox
          label={
            <>
              I understand that any data beyond limit of new plan will be permanently deleted and cannot be recovered.{' '}
              <Required />
            </>
          }
          checked={isChecked}
          onChange={() => setIsChecked((prev) => !prev)}
        />
      </div>
    </BaseModal>
  );
};

export default memo(DowngradeModal);
